import { Injectable } from "@angular/core";
import { AuthService } from "./auth.service";
import { SessionService } from "./session.service";

/**
 * Tracks opened application tabs in order to support a "logout on leave" mode.
 *
 * When the last application tab is closed, the logout cannot be executed
 * immediately because no JavaScript remains running. Instead, the next time
 * the application is opened, we detect that all tabs had previously been
 * closed and perform the logout at startup.
 */
@Injectable({
  providedIn: "root",
})
export class TabSessionService {
  // Unique identifier for the current browser tab.
  // sessionStorage survives page reloads but is isolated per tab.
  private readonly TAB_ID = sessionStorage.getItem("tab-id") ?? crypto.randomUUID();

  // Stores the list of currently opened application tabs.
  private readonly ACTIVE_TABS_KEY = "active-tabs";

  // Timestamp of when the last known tab was closed.
  private readonly LAST_CLOSE_KEY = "last-close";

  private initialized = false;

  constructor(private authService: AuthService, private sessionService: SessionService) {
    sessionStorage.setItem("tab-id", this.TAB_ID);
  }

  /**
   * Starts monitoring tab activity.
   * Only used for users that must be logged out when all application tabs are closed.
   */
  public startWatcher(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    this.handleStartup();

    window.addEventListener("beforeunload", this.beforeUnloadHandler);
  }

  /**
   * Stops monitoring tab activity.
   */
  public stopWatcher(): void {
    if (!this.initialized) {
      return;
    }

    this.unregisterTab();

    window.removeEventListener("beforeunload", this.beforeUnloadHandler);

    this.initialized = false;
  }

  /**
   * Triggered when the browser starts unloading the page
   * (tab close, browser close, navigation or refresh).
   */
  private beforeUnloadHandler = (): void => {
    this.unregisterTab();
  };

  /**
   * Executed when the application starts.
   *
   * If no other application tab is currently registered and the last tab
   * was closed more than 5 seconds ago, we consider this a fresh launch
   * after all tabs were closed and clear the user's session.
   *
   * The delay prevents page refreshes from being mistaken for a real exit.
   */
  private handleStartup(): void {
    const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;

    const isReload = navigation?.type === "reload";

    const tabs = this.getTabs();

    if (!isReload) {
      const lastClose = Number(localStorage.getItem(this.LAST_CLOSE_KEY));

      const noActiveTabs = Object.keys(tabs).length === 0;

      if (noActiveTabs && lastClose && Date.now() - lastClose > 5000) {
        if (this.sessionService.logoutOnLeave) {
          this.authService.logout();
        }
      }
    }

    this.registerTab();
  }

  /**
   * Registers the current tab as active.
   */
  private registerTab(): void {
    const tabs = this.getTabs();

    tabs[this.TAB_ID] = Date.now();

    localStorage.setItem(this.ACTIVE_TABS_KEY, JSON.stringify(tabs));
  }

  /**
   * Removes the current tab from the active tab registry.
   *
   * If this was the last known application tab, store the timestamp.
   * This timestamp is later used to distinguish a real application exit
   * from a simple page refresh.
   */
  private unregisterTab(): void {
    const tabs = this.getTabs();

    delete tabs[this.TAB_ID];

    localStorage.setItem(this.ACTIVE_TABS_KEY, JSON.stringify(tabs));

    if (Object.keys(tabs).length === 0) {
      localStorage.setItem(this.LAST_CLOSE_KEY, Date.now().toString());
    }
  }

  /**
   * Returns the current list of active application tabs.
   */
  private getTabs(): Record<string, number> {
    return JSON.parse(localStorage.getItem(this.ACTIVE_TABS_KEY) ?? "{}");
  }
}
